// Cart functionality
class CartManager {
  constructor() {
    if (window.debugLog) window.debugLog('CartManager constructor called');
    this.cartCount = 0;
    this.cartItems = [];
    this.init();
  }
  
  init() {
    if (window.debugLog) window.debugLog('CartManager.init start');
    this.updateCartDisplay();
    this.loadCartFromStorage();
  }
  
  addToCart(productId, variantId, quantity = 1, productData = {}) {
    const debugData = { productId, variantId, quantity, productData };
    console.log('CartManager.addToCart called with:', debugData);
    if (window.debugLog) window.debugLog('CartManager.addToCart called', debugData);
    
    // For bundles with selected flavors: only prevent adding the EXACT same
    // bundle composition twice. A bundle WITHOUT flavors and the same bundle
    // WITH flavors are treated as distinct cart items and both remain.
    if (productData.type === 'bundle' && Array.isArray(productData.bundleItems) && productData.bundleItems.length > 0) {
      const hasSelectedItems = productData.bundleItems.some(item => (parseInt(item?.quantity, 10) || 0) > 0);
      if (hasSelectedItems) {
        const bundleKey = this.createBundleKey(productData.bundleItems);
        const exactMatch = this.cartItems.find(item => 
          item.productId === productId && 
          item.variantId === variantId && 
          item.type === 'bundle' &&
          item.bundleKey === bundleKey
        );
        
        if (exactMatch) {
          console.log('Exact bundle already exists, updating quantity instead of adding duplicate');
          exactMatch.quantity += quantity;
          this.updateCartCount();
          this.saveCartToStorage();
          return;
        }
      }
    }
    
    // Check if item already exists in cart
    let existingItem;
    if (productData.type === 'bundle' && productData.bundleItems) {
      // For bundle items, check if exact same bundle composition exists
      const bundleKey = this.createBundleKey(productData.bundleItems);
      existingItem = this.cartItems.find(item => 
        item.productId === productId && 
        item.variantId === variantId && 
        item.type === 'bundle' &&
        item.bundleKey === bundleKey
      );
    } else {
      // For regular items, use standard comparison
      existingItem = this.cartItems.find(item => 
        item.productId === productId && 
        item.variantId === variantId &&
        item.type !== 'bundle'
      );
    }
    
    if (existingItem) {
      existingItem.quantity += quantity;
      existingItem.promoEligible = true;
      console.log('Updated existing item:', existingItem);
      if (window.debugLog) window.debugLog('Updated existing item', existingItem);
    } else {
      const newItem = {
        productId,
        variantId,
        quantity,
        ...productData
      };

      newItem.promoEligible = true;
      
      // Add bundle key for bundle items
      if (productData.type === 'bundle' && productData.bundleItems) {
        newItem.bundleKey = this.createBundleKey(productData.bundleItems);
      }
      
      this.cartItems.push(newItem);
      console.log('Added new item:', newItem);
      if (window.debugLog) window.debugLog('Added new item', newItem);
    }
    
    console.log('Cart items after add:', this.cartItems);
    if (window.debugLog) window.debugLog('Cart items after add', this.cartItems);
    
    this.updateCartCount();
    this.saveCartToStorage();
    this.showAddToCartSuccess();
    
    // Show price animation - calculate total price
    const totalPrice = (productData.price || 0) * quantity;
    this.showPriceAnimation(totalPrice);
    
    // Trigger custom event for button animation
    window.dispatchEvent(new CustomEvent('cartItemAdded', {
      detail: { productId, variantId, quantity, productData }
    }));
    
    console.log('Cart count after update:', this.cartCount);
    if (window.debugLog) window.debugLog('Cart count after update', this.cartCount);
  }
  
  updateCartCount() {
    this.cartCount = this.cartItems.reduce((total, item) => total + item.quantity, 0);
    this.updateCartDisplay();
  }
  
  updateCartDisplay() {
    // Update all cart count badges (desktop + mobile)
    const badgeNodes = document.querySelectorAll('[data-cart-count]');
    console.log('Updating cart display. Count:', this.cartCount, 'Badges:', badgeNodes.length);
    if (window.debugLog) window.debugLog('Updating cart display', { count: this.cartCount, badges: badgeNodes.length });

    if (badgeNodes.length > 0) {
      badgeNodes.forEach((el) => {
        el.textContent = this.cartCount;
        el.style.display = this.cartCount > 0 ? 'inline' : 'none';
      });
      console.log('Cart count updated in UI to:', this.cartCount);
      if (window.debugLog) window.debugLog('SUCCESS: Cart count updated in UI', this.cartCount);
    } else {
      // Fallback to legacy selection
      const fallback = document.getElementById('cart-count') || document.querySelector('#cart-count') || document.querySelector('.sh-badge');
      if (fallback) {
        fallback.textContent = this.cartCount;
        fallback.style.display = this.cartCount > 0 ? 'inline' : 'none';
      } else {
        console.error('Cart count element not found!');
        if (window.debugLog) window.debugLog('ERROR: Cart count element not found');
      }
    }
    
    // Update floating cart button
    this.updateFloatingCart();
  }
  
  updateFloatingCart() {
    const floatingCart = document.getElementById('floating-cart-button');
    if (floatingCart) {
      // Hide on cart page
      if (window.location.pathname.includes('/cart') || 
          document.body.classList.contains('template-cart') ||
          document.body.classList.contains('cart-page')) {
        floatingCart.style.display = 'none';
        floatingCart.style.setProperty('display', 'none', 'important');
        return;
      }
      
      if (this.cartCount > 0) {
        floatingCart.style.display = 'block';
        // Add animation class for smooth appearance
        floatingCart.classList.add('ph-floating-cart--show');
      } else {
        floatingCart.style.display = 'none';
        floatingCart.classList.remove('ph-floating-cart--show');
      }
    }
  }
  
  showPriceAnimation(price) {
    if (!price) return;
    
    const priceDisplay = document.getElementById('cart-price-display');
    const priceText = document.querySelector('.ph-floating-cart__price-text');
    
    if (priceDisplay && priceText) {
      priceText.textContent = `+${price}`;
      priceDisplay.style.display = 'block';
      
      // Reset animation
      priceDisplay.style.animation = 'none';
      priceDisplay.offsetHeight; // Trigger reflow
      priceDisplay.style.animation = 'priceShow 2s ease-out';
      
      // Hide after animation
      setTimeout(() => {
        priceDisplay.style.display = 'none';
      }, 2000);
    }
  }
  
  saveCartToStorage() {
    try {
      if (this.cartItems.length === 0) {
        // Clear storage when cart is empty
        localStorage.removeItem('ph-cart');
        console.log('[CartManager] Cleared cart from storage');
        if (window.debugLog) window.debugLog('Cleared cart from storage');
      } else {
        const cartData = JSON.stringify(this.cartItems);
        localStorage.setItem('ph-cart', cartData);
        console.log('[CartManager] Saved cart to storage:', { 
          count: this.cartItems.length, 
          dataLength: cartData.length,
          timestamp: new Date().toISOString()
        });
        if (window.debugLog) window.debugLog('Saved cart to storage', { count: this.cartItems.length });
        
        // Verify save worked
        const verify = localStorage.getItem('ph-cart');
        if (verify !== cartData) {
          console.error('[CartManager] Storage verification failed!');
        } else {
          console.log('[CartManager] Storage verification successful');
        }
      }
    } catch (e) {
      console.error('[CartManager] Failed to save cart', e);
      if (window.debugLog) window.debugLog('ERROR: Failed to save cart', { error: String(e) });
    }
  }
  
  loadCartFromStorage() {
    console.log('[CartManager] Loading cart from storage...');
    
    // Try new key first, then old key for migration
    let savedCart = localStorage.getItem('ph-cart');
    if (!savedCart) {
      savedCart = localStorage.getItem('shopify_cart');
      if (savedCart) {
        console.log('[CartManager] Migrating from old cart storage key');
        if (window.debugLog) window.debugLog('Migrating from old cart storage key');
        localStorage.removeItem('shopify_cart'); // Remove old key
      }
    }
    
    console.log('[CartManager] Loading cart from storage:', savedCart ? `${savedCart.length} chars` : 'null');
    if (window.debugLog) window.debugLog('Loading cart from storage', savedCart);
    
    if (savedCart) {
      let parsedItems = [];
      try {
        parsedItems = JSON.parse(savedCart);
      } catch(e) {
        console.error('Failed to parse saved cart', e);
        if (window.debugLog) window.debugLog('ERROR: Failed to parse saved cart', { error: String(e), savedCart });
        parsedItems = [];
      }
      
      // Migrate old format to new format + promo flags for 2+1 sets
      this.cartItems = parsedItems.map(item => {
        let next = item;
        if (typeof item.price === 'string' && item.price.includes('zł')) {
          const priceText = item.price;
          const price = parseFloat(priceText.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
          next = { ...item, price: price, priceText: priceText };
        }
        return CartManager.normalizePromoFlags(next);
      });
      
      console.log('[CartManager] Loaded cart items:', this.cartItems.length, 'items');
      if (window.debugLog) window.debugLog('Loaded cart items', this.cartItems);
      this.updateCartCount();
      this.saveCartToStorage(); // Save in new format
    } else {
      console.log('[CartManager] No saved cart found');
    }
    
    console.log('[CartManager] Cart initialization complete:', {
      itemsCount: this.cartItems.length,
      cartCount: this.cartCount
    });
  }
  
  getCartItems() {
    return this.cartItems;
  }
  
  createBundleKey(bundleItems) {
    if (!bundleItems || !Array.isArray(bundleItems)) {
      return 'empty-bundle';
    }
    
    // Create a sorted array of item signatures
    const itemSignatures = bundleItems
      .filter(item => item.quantity > 0) // Only include items with quantity > 0
      .map(item => `${item.id}:${item.quantity}`)
      .sort(); // Sort to ensure consistent key regardless of order
    
    return itemSignatures.join('|') || 'empty-bundle';
  }
  
  showAddToCartSuccess() {
    // No notification - just animation on button
  }

  static normalizePromoFlags(item) {
    if (!item) return item;
    const next = { ...item, promoEligible: true };
    if (next.isBundleSingle === 'true') next.isBundleSingle = true;
    return next;
  }
}

// Initialize cart manager when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize cart manager
  const cartManager = new CartManager();
  
  // Make cart manager globally available
  window.cartManager = cartManager;
  
  // Listen for CartAddEvent to sync with Shopify cart
  document.addEventListener('cart-update', function(event) {
    if (event.detail && event.detail.data && !event.detail.data.didError) {
      const { productId, variantId, itemCount } = event.detail.data;
      if (productId && variantId && itemCount) {
        console.log('CartAddEvent received, adding to custom cart:', { productId, variantId, itemCount });
        if (window.debugLog) window.debugLog('CartAddEvent received', { productId, variantId, itemCount });
        
        // Get product data from the page
        const productTitle = document.querySelector('.product__title, .ph-product-details__title')?.textContent || 'Product';
        const priceEl = document.querySelector('.price, .ph-product-details__price-regular, .ph-product-details__price-sale');
        const productPriceText = priceEl?.textContent || '0,00 zł';
        const productPrice = parseFloat(productPriceText.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        const productImage = document.querySelector('.product__media img, .ph-product-gallery__main-img')?.getAttribute('src') || '';
        
        // Add to custom cart
        cartManager.addToCart(productId, variantId, itemCount, {
          title: productTitle,
          price: productPrice,
          priceText: productPriceText,
          image: productImage,
          type: 'single'
        });
      }
    }
  });
  
  // Debug: Check if cart manager is working
  console.log('CartManager initialized:', window.cartManager);
  if (window.debugLog) window.debugLog('SUCCESS: CartManager initialized', { cartManager: window.cartManager });
});

// Also try to initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  // DOM is still loading, wait for DOMContentLoaded
  if (window.debugLog) window.debugLog('DOM is loading, waiting for DOMContentLoaded');
} else {
  // DOM is already loaded, initialize immediately
  const cartManager = new CartManager();
  window.cartManager = cartManager;
  
  // Listen for CartAddEvent to sync with Shopify cart
  document.addEventListener('cart-update', function(event) {
    if (event.detail && event.detail.data && !event.detail.data.didError) {
      const { productId, variantId, itemCount } = event.detail.data;
      if (productId && variantId && itemCount) {
        console.log('CartAddEvent received, adding to custom cart:', { productId, variantId, itemCount });
        if (window.debugLog) window.debugLog('CartAddEvent received', { productId, variantId, itemCount });
        
        // Get product data from the page
        const productTitle = document.querySelector('.product__title, .ph-product-details__title')?.textContent || 'Product';
        const priceEl = document.querySelector('.price, .ph-product-details__price-regular, .ph-product-details__price-sale');
        const productPriceText = priceEl?.textContent || '0,00 zł';
        const productPrice = parseFloat(productPriceText.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        const productImage = document.querySelector('.product__media img, .ph-product-gallery__main-img')?.getAttribute('src') || '';
        
        // Add to custom cart
        cartManager.addToCart(productId, variantId, itemCount, {
          title: productTitle,
          price: productPrice,
          priceText: productPriceText,
          image: productImage,
          type: 'single'
        });
      }
    }
  });
  
  console.log('CartManager initialized immediately:', window.cartManager);
  if (window.debugLog) window.debugLog('SUCCESS: CartManager initialized immediately', { cartManager: window.cartManager });
}

// When the browser restores a page from bfcache (back/forward navigation),
// DOMContentLoaded does NOT fire again. Re-sync cart count from localStorage
// so the header badge always reflects the latest state.
window.addEventListener('pageshow', function(e) {
  if (e.persisted && window.cartManager) {
    window.cartManager.loadCartFromStorage();
    console.log('[CartManager] pageshow (bfcache restore) – cart reloaded from storage');
  }
});
